import { Link } from "react-router-dom";
import { useReducedMotion } from "@/lib/useReducedMotion";
import { Reveal } from "@/components/Reveal";
import { useSeo } from "@/lib/seo";
import { useLang, lp } from "@/lib/i18n";
import { ASSETS, VIDEO, POSTER } from "@/assets";

const jost = { fontFamily: "Jost, sans-serif" };

const T = {
  fr: {
    seoTitle: "Maison Partita — Haute parfumerie & maroquinerie",
    seoDesc: "Maison de haute parfumerie et de maroquinerie. L'art de l'équilibre, dans le plus grand des silences. Crisopea, Domino.",
    heroTitle: ["L'éloquence", "de l'invisible."],
    heroText: "Maison de haute parfumerie et de maroquinerie. L'art de l'équilibre, dans le plus grand des silences.",
    heroCta: "Découvrir la maison →",
    elogeKicker: "L'éloge du temps",
    eloge1: "Nous cultivons l'art des contrastes. Entre l'intensité d'une essence absolue et la pureté d'une ligne géométrique, chaque création est une quête d'",
    elogeEm: "harmonie souveraine",
    eloge2: ".",
    perfume: "Parfum",
    crisopeaText: "L'incandescence des origines. Une collection façonnée par le feu et les astres.",
    dominoText: "La grâce de l'équilibre. L'épure géométrique où la lumière devient parfum.",
    leather: "Le cuir",
    leatherTitle: "Maroquinerie",
    leatherText: "L'architecture du cuir. Le geste lent de l'artisan au service d'une ligne intemporelle.",
    discover: "Découvrir →",
    closing1: "Partita",
    closing2: " — une suite de mouvements liés par une même forme.",
    altGold: "Or en fusion, l'univers Crisopea",
    altDomino: "Équilibre clair, l'univers Domino",
    altLeather: "Grain de cuir et ferrure dorée, la maroquinerie de la maison",
  },
  en: {
    seoTitle: "Maison Partita — Haute Parfumerie & Leather Goods",
    seoDesc: "A house of haute parfumerie and fine leather goods. The art of balance, in the greatest of silences. Crisopea, Domino.",
    heroTitle: ["The eloquence", "of the invisible."],
    heroText: "A house of haute parfumerie and fine leather goods. The art of balance, in the greatest of silences.",
    heroCta: "Discover the house →",
    elogeKicker: "In praise of time",
    eloge1: "We cultivate the art of contrasts. Between the intensity of an absolute essence and the purity of a geometric line, every creation is a quest for ",
    elogeEm: "sovereign harmony",
    eloge2: ".",
    perfume: "Perfume",
    crisopeaText: "The incandescence of origins. A collection forged by fire and the stars.",
    dominoText: "The grace of balance. Geometric purity where light becomes perfume.",
    leather: "Leather",
    leatherTitle: "Leather Goods",
    leatherText: "The architecture of leather. The artisan's unhurried hand in service of a timeless line.",
    discover: "Discover →",
    closing1: "Partita",
    closing2: " — a suite of movements bound by a single form.",
    altGold: "Molten gold, the world of Crisopea",
    altDomino: "Luminous balance, the world of Domino",
    altLeather: "Leather grain and gilded hardware, the house's leather goods",
  },
};

const Cta = ({ children, tone = "dark" }) => {
  const light = tone === "light";
  return (
    <span
      className="inline-block text-[11px] tracking-[0.32em] uppercase pb-1 border-b transition-colors duration-700"
      style={{
        color: light ? "rgba(245,242,235,0.85)" : "#121110",
        borderColor: light ? "#C9A86A" : "rgba(18,17,16,0.35)",
        ...(light ? {} : jost),
      }}
    >
      {children}
    </span>
  );
};

export default function Home() {
  const lang = useLang();
  const t = T[lang];
  useSeo(t.seoTitle, t.seoDesc);
  const reduce = useReducedMotion();

  return (
    <div>
      {/* HERO — parchemin lumineux, vidéo cadrée, titre en surimpression */}
      <section className="relative">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-10 pt-16 pb-20 lg:py-28">
          <div className="lg:grid lg:grid-cols-12 lg:items-center lg:gap-y-0">
            <Reveal className="lg:col-start-1 lg:col-span-6 lg:row-start-1 lg:z-10 order-1">
              <p className="text-[11px] tracking-[0.34em] uppercase" style={{ color: "#8a8578" }}>Maison Partita</p>
              <h1 className="mt-7 text-5xl md:text-6xl lg:text-7xl font-light leading-[1.02] tracking-[-0.01em]" style={{ color: "#121110" }}>
                {t.heroTitle[0]}<br />{t.heroTitle[1]}
              </h1>
              <p className="mt-9 max-w-sm text-[17px] leading-[1.75]" style={{ ...jost, color: "#5c5a56" }}>
                {t.heroText}
              </p>
              <Link to={lp(lang, "/la-maison")} data-testid="home-maison-link" className="group mt-10 inline-block">
                <Cta>{t.heroCta}</Cta>
              </Link>
            </Reveal>

            <Reveal delay={0.2} className="lg:col-start-6 lg:col-span-7 lg:row-start-1 mt-12 lg:mt-0">
              <div className="overflow-hidden" style={{ border: "1px solid #E4DFD5" }}>
                <video
                  src={VIDEO.hero}
                  poster={POSTER.hero}
                  autoPlay={!reduce}
                  muted
                  loop
                  playsInline
                  preload="none"
                  aria-hidden="true"
                  tabIndex={-1}
                  className="w-full h-full object-cover"
                  style={{ aspectRatio: "16/10" }}
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MANIFESTE — l'éloge du temps */}
      <section className="max-w-2xl mx-auto px-5 sm:px-10 py-32 text-center">
        <Reveal>
          <p className="text-[11px] tracking-[0.34em] uppercase" style={{ color: "#8a8578" }}>{t.elogeKicker}</p>
          <p className="mt-10 text-2xl sm:text-3xl leading-[1.55] font-light" style={{ color: "#2a2825" }}>
            {t.eloge1}
            <span className="italic" style={{ color: "#B08D4A" }}>{t.elogeEm}</span>
            {t.eloge2}
          </p>
        </Reveal>
      </section>

      {/* LES DEUX PARFUMS — bento asymétrique */}
      <section className="max-w-[1500px] mx-auto px-5 sm:px-10 pb-8">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-start">
          {/* Crisopea — sombre, encadré, or, Cormorant */}
          <Reveal>
            <Link to={lp(lang, "/crisopea")} data-testid="entry-crisopea"
              className="group relative block overflow-hidden min-h-[62vh] lg:min-h-[76vh] flex flex-col justify-end p-10 md:p-12"
              style={{ background: "#171614" }}>
              <div className="absolute inset-0 overflow-hidden">
                <img src={ASSETS.heroGold} alt={t.altGold}
                  className="w-full h-full object-cover opacity-45 group-hover:opacity-60 group-hover:scale-[1.03] transition-all duration-[1400ms] ease-out" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #171614 12%, rgba(23,22,20,0.45) 55%, transparent)" }} />
              </div>
              <div className="relative z-10">
                <p className="text-[11px] tracking-[0.34em] uppercase" style={{ color: "#C9A86A" }}>I · {t.perfume}</p>
                <h2 className="mt-4 text-4xl sm:text-5xl font-light tracking-[0.02em]" style={{ color: "#F5F2EB" }}>Crisopea</h2>
                <p className="mt-5 max-w-sm text-[16px] leading-[1.7]" style={{ color: "rgba(245,242,235,0.72)" }}>
                  {t.crisopeaText}
                </p>
                <span className="mt-8 inline-block"><Cta tone="light">{t.discover}</Cta></span>
              </div>
            </Link>
          </Reveal>

          {/* Domino — clair, aéré, géométrique, Jost — décalé pour l'asymétrie */}
          <Reveal delay={0.15}>
            <Link to={lp(lang, "/domino")} data-testid="entry-domino"
              className="group relative block overflow-hidden min-h-[54vh] lg:min-h-[64vh] lg:mt-16 flex flex-col justify-end p-10 md:p-12"
              style={{ background: "#ffffff", border: "1px solid #E4DFD5", ...jost }}>
              <div className="absolute inset-0 overflow-hidden">
                <img src={ASSETS.dominoLight} alt={t.altDomino}
                  className="w-full h-full object-cover opacity-70 group-hover:opacity-85 group-hover:scale-[1.03] transition-all duration-[1400ms] ease-out" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #ffffff 10%, rgba(255,255,255,0.35) 50%, transparent)" }} />
              </div>
              <div className="relative z-10">
                <p className="text-[11px] tracking-[0.34em] uppercase" style={{ color: "#6b6862" }}>II · {t.perfume}</p>
                <h2 className="mt-4 text-4xl sm:text-5xl font-light tracking-[0.06em] uppercase" style={{ color: "#1a1a1a" }}>Domino</h2>
                <p className="mt-5 max-w-sm text-[16px] leading-[1.7]" style={{ color: "#4a4842" }}>
                  {t.dominoText}
                </p>
                <span className="mt-8 inline-block"><Cta>{t.discover}</Cta></span>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* MAROQUINERIE — chapitre clair et chaud, paysage */}
      <section className="mt-20" style={{ background: "#EAE5DC" }}>
        <div className="max-w-[1500px] mx-auto px-5 sm:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-7">
            <Link to={lp(lang, "/maroquinerie")} data-testid="entry-maroquinerie" className="group block overflow-hidden" style={{ border: "1px solid #DAD3C6" }}>
              <img src={ASSETS.maroquinerieHero} alt={t.altLeather}
                className="w-full object-cover group-hover:scale-[1.03] transition-transform duration-[1400ms] ease-out"
                style={{ aspectRatio: "16/9" }} />
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="text-[11px] tracking-[0.34em] uppercase" style={{ ...jost, color: "#8a8578" }}>III · {t.leather}</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-light tracking-[0.01em]" style={{ color: "#121110" }}>{t.leatherTitle}</h2>
            <p className="mt-6 max-w-sm text-[17px] leading-[1.75]" style={{ ...jost, color: "#5c5a56" }}>
              {t.leatherText}
            </p>
            <Link to={lp(lang, "/maroquinerie")} className="group mt-9 inline-block"><Cta>{t.discover}</Cta></Link>
          </Reveal>
        </div>
      </section>

      {/* Clôture — le vide */}
      <section className="max-w-2xl mx-auto px-5 sm:px-10 py-32 text-center">
        <Reveal>
          <p className="text-2xl sm:text-3xl font-light leading-[1.5]" style={{ color: "#3a3833" }}>
            <em>{t.closing1}</em>{t.closing2}
          </p>
        </Reveal>
      </section>
    </div>
  );
}
